import { Eye, Lock, Users, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { EmployeeNoteVisibility } from '@gokrew/models/employee-notes';
import { Tone } from '@gokrew/models/ui.types';
import { Chip } from '@/components/Chip';
import { cn } from '@/lib/utils';
import styles from './NoteVisibilityBadge.module.css';

const ICON: Record<EmployeeNoteVisibility, LucideIcon> = {
  [EmployeeNoteVisibility.HR_ONLY]: Lock,
  [EmployeeNoteVisibility.HR_AND_MANAGERS]: Users,
  [EmployeeNoteVisibility.EVERYONE]: Eye,
};

const TONE: Record<EmployeeNoteVisibility, Tone> = {
  [EmployeeNoteVisibility.HR_ONLY]: Tone.SECONDARY,
  [EmployeeNoteVisibility.HR_AND_MANAGERS]: Tone.PRIMARY,
  [EmployeeNoteVisibility.EVERYONE]: Tone.SUCCESS,
};

export interface NoteVisibilityBadgeProps {
  visibility?: EmployeeNoteVisibility | null;
  className?: string;
}

/**
 * Who can read an employee note: a lock (HR only), a team (HR + managers)
 * or an eye (everyone, including the employee). Shared by the company
 * portal note cards and the employee My team and shared-notes pages; the
 * tooltip spells out the level. Strings: `notes.json` > `note_visibility`.
 */
export const NoteVisibilityBadge = ({
  visibility,
  className,
}: NoteVisibilityBadgeProps) => {
  const { t } = useTranslation(TranslationNS.Notes);
  const level = visibility ?? EmployeeNoteVisibility.HR_ONLY;
  const Icon = ICON[level];
  return (
    <Chip
      type={TONE[level]}
      size="sm"
      className={cn(styles.badge, className)}
      title={t(`note_visibility.description.${level}`)}
    >
      <Icon aria-hidden className={styles.icon} />
      {t(`note_visibility.short.${level}`)}
    </Chip>
  );
};

export default NoteVisibilityBadge;
